import { Suspense, lazy } from 'react'
import { Routes, Route } from 'react-router-dom'
import { AnimatePresence } from 'framer-motion'
import { useLocation } from 'react-router-dom'
import { Toaster } from '@/components/ui/toaster'
import PageLoader from '@/components/ui/PageLoader'
import Layout from '@/components/layout/Layout'
import AuthLayout from '@/components/layout/AuthLayout'
import { QueryClientProvider, QueryClient } from '@tanstack/react-query';
import { AuthenticationProvider, WalletProvider, LocationProvider, OrdersProvider } from './providers'

// Lazy-loaded Auth Pages
const LoginPage = lazy(() => import('@/pages/auth/LoginPage'))
const RegisterPage = lazy(() => import('@/pages/auth/RegisterPage'))
const OnboardingPage = lazy(() => import('@/pages/auth/OnboardingPage'))
const ForgotPasswordPage = lazy(() => import('@/pages/auth/ForgotPasswordPage'))
const ResetPasswordPage = lazy(() => import('@/pages/auth/ResetPasswordPage'))
const RequestOTPPage = lazy(() => import('@/pages/auth/RequestOTPPage'))
const VerifyOTPPage = lazy(() => import('@/pages/auth/VerifyOTPPage'))
const ChangePasswordPage = lazy(() => import('@/pages/auth/ChangePasswordPage'))

// Lazy-loaded Main Pages
const DashboardPage = lazy(() => import('@/pages/DashboardPage'))
const ActiveOrdersPage = lazy(() => import('@/pages/ActiveOrdersPage'))
const EarningsDashboardPage = lazy(() => import('@/pages/EarningsDashboardPage'))
const EmergencyPage = lazy(() => import('@/pages/EmergencyPage'))
const ProfilePage = lazy(() => import('@/pages/ProfilePage'))
const WalletPage = lazy(() => import('@/pages/WalletPage'))
const OrdersPage = lazy(() => import('@/pages/OrdersPage'))
const LeaderboardPage = lazy(() => import('@/pages/LeaderboardPage'))
const SupportPage = lazy(() => import('@/pages/SupportPage'))
const DocumentsPage = lazy(() => import('@/pages/DocumentsPage'))
const SettingsPage = lazy(() => import('@/pages/SettingsPage'))

// Lazy-loaded Gamification Pages
const BadgesPage = lazy(() => import('@/pages/gamification/BadgesPage'))
const ChallengesPage = lazy(() => import('@/pages/gamification/ChallengesPage'))
const TeamsPage = lazy(() => import('@/pages/gamification/TeamsPage'))

// Lazy-loaded Incentives Page
const IncentivesPage = lazy(() => import('@/pages/IncentivesPage'))

// Lazy-loaded Tier Rewards Page
const TierRewardsPage = lazy(() => import('@/pages/TierRewardsPage'))

// Lazy-loaded Bonus History Page
const BonusHistoryPage = lazy(() => import('@/pages/BonusHistoryPage'))

// Lazy-loaded API Test Page (for development)
const APITestPage = lazy(() => import('@/pages/APITestPage'))

// Lazy-loaded Support Pages
const ComplaintsPage = lazy(() => import('@/pages/support/ComplaintsPage'))
const HelpCenterPage = lazy(() => import('@/pages/support/HelpCenterPage'))
const ContactSupportPage = lazy(() => import('@/pages/support/ContactSupportPage'))

const queryClient = new QueryClient()

function App() {
  const location = useLocation()

  return (
    <QueryClientProvider client={queryClient}>
      <Suspense fallback={<PageLoader text="Loading application..." />}>
        <AuthenticationProvider>
          <LocationProvider>
            <AnimatePresence mode="wait">
              <Routes location={location} key={location.pathname}>
                {/* Auth Routes */}
                <Route path="/auth" element={<AuthLayout />}>
                  <Route path="login-password" element={
                    <Suspense fallback={<PageLoader text="Loading login..." />}>
                      <LoginPage />
                    </Suspense>
                  } />
                  <Route path="register" element={
                    <Suspense fallback={<PageLoader text="Loading registration..." />}>
                      <RegisterPage />
                    </Suspense>
                  } />
                  <Route path="onboarding" element={
                    <Suspense fallback={<PageLoader text="Loading onboarding..." />}>
                      <OnboardingPage />
                    </Suspense>
                  } />
                  <Route path="forgot-password" element={
                    <Suspense fallback={<PageLoader text="Loading forgot password..." />}>
                      <ForgotPasswordPage />
                    </Suspense>
                  } />
                  <Route path="reset-password" element={
                    <Suspense fallback={<PageLoader text="Loading reset password..." />}>
                      <ResetPasswordPage />
                    </Suspense>
                  } />
                  <Route path="login" element={
                    <Suspense fallback={<PageLoader text="Loading OTP request..." />}>
                      <RequestOTPPage />
                    </Suspense>
                  } />
                  <Route path="verify-otp" element={
                    <Suspense fallback={<PageLoader text="Loading OTP verification..." />}>
                      <VerifyOTPPage />
                    </Suspense>
                  } />
                </Route>

                {/* Main App Routes */}
                <Route path="/" element={<Layout />}>
                  <Route index element={
                    <Suspense fallback={<PageLoader text="Loading dashboard..." />}>
                      <DashboardPage />
                    </Suspense>
                  } />
                  <Route path="dashboard" element={
                    <Suspense fallback={<PageLoader text="Loading dashboard..." />}>
                      <DashboardPage />
                    </Suspense>
                  } />
                  <Route path="profile" element={
                    <Suspense fallback={<PageLoader text="Loading profile..." />}>
                      <ProfilePage />
                    </Suspense>
                  } />
                  <Route path="wallet" element={
                    <Suspense fallback={<PageLoader text="Loading wallet..." />}>
                      <WalletProvider>
                        <WalletPage />
                      </WalletProvider>
                    </Suspense>
                  } />
                  <Route path="active-orders" element={
                    <Suspense fallback={<PageLoader text="Loading active orders..." />}>
                      <OrdersProvider>
                        <ActiveOrdersPage />
                      </OrdersProvider>
                    </Suspense>
                  } />
                  <Route path="earnings" element={
                    <Suspense fallback={<PageLoader text="Loading earnings dashboard..." />}>
                      <EarningsDashboardPage />
                    </Suspense>
                  } />
                  <Route path="emergency" element={
                    <Suspense fallback={<PageLoader text="Loading emergency settings..." />}>
                      <EmergencyPage />
                    </Suspense>
                  } />
                  <Route path="orders" element={
                    <Suspense fallback={<PageLoader text="Loading orders..." />}>
                      <OrdersProvider>
                        <OrdersPage />
                      </OrdersProvider>
                    </Suspense>
                  } />
                  <Route path="leaderboard" element={
                    <Suspense fallback={<PageLoader text="Loading leaderboard..." />}>
                      <LeaderboardPage />
                    </Suspense>
                  } />
                  <Route path="documents" element={
                    <Suspense fallback={<PageLoader text="Loading documents..." />}>
                      <DocumentsPage />
                    </Suspense>
                  } />
                  <Route path="settings" element={
                    <Suspense fallback={<PageLoader text="Loading settings..." />}>
                      <SettingsPage />
                    </Suspense>
                  } />
                  <Route path="change-password" element={
                    <Suspense fallback={<PageLoader text="Loading change password..." />}>
                      <ChangePasswordPage />
                    </Suspense>
                  } />

                  {/* Gamification Routes */}
                  <Route path="badges" element={
                    <Suspense fallback={<PageLoader text="Loading badges..." />}>
                      <BadgesPage />
                    </Suspense>
                  } />
                  <Route path="challenges" element={
                    <Suspense fallback={<PageLoader text="Loading challenges..." />}>
                      <ChallengesPage />
                    </Suspense>
                  } />
                  <Route path="teams" element={
                    <Suspense fallback={<PageLoader text="Loading teams..." />}>
                      <TeamsPage />
                    </Suspense>
                  } />

                  {/* Incentives Route */}
                  <Route path="incentives" element={
                    <Suspense fallback={<PageLoader text="Loading incentives..." />}>
                      <IncentivesPage />
                    </Suspense>
                  } />

                {/* Tier Rewards Route */}
                <Route path="tier-rewards" element={
                  <Suspense fallback={<PageLoader text="Loading tier rewards..." />}>
                    <TierRewardsPage />
                  </Suspense>
                } />

                {/* Bonus History Route */}
                <Route path="bonuses" element={
                  <Suspense fallback={<PageLoader text="Loading bonus history..." />}>
                    <BonusHistoryPage />
                  </Suspense>
                } />

                {/* API Test Page (Development) */}
                <Route path="api-test" element={
                  <Suspense fallback={<PageLoader text="Loading API tests..." />}>
                    <APITestPage />
                  </Suspense>
                } />

                  {/* Support Routes */}
                  <Route path="support" element={
                    <Suspense fallback={<PageLoader text="Loading support..." />}>
                      <SupportPage />
                    </Suspense>
                  } />
                  <Route path="complaints" element={
                    <Suspense fallback={<PageLoader text="Loading complaints..." />}>
                      <ComplaintsPage />
                    </Suspense>
                  } />
                  <Route path="help" element={
                    <Suspense fallback={<PageLoader text="Loading help center..." />}>
                      <HelpCenterPage />
                    </Suspense>
                  } />
                  <Route path="contact" element={
                    <Suspense fallback={<PageLoader text="Loading contact support..." />}>
                      <ContactSupportPage />
                    </Suspense>
                  } />
                </Route>
              </Routes>
            </AnimatePresence>
          </LocationProvider>
        </AuthenticationProvider>
      </Suspense>
      <Toaster />
    </QueryClientProvider>
  )
}

export default App